import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TaskRequest } from '../../../models/api/todo/task-request';
import { TaskResponse } from '../../../models/api/todo/task-response';
import { bothOrNoneRequired } from '../../../validators/both-or-none-required';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { InputComponent } from '../../core/controls/input.component';
import { RangeComponent } from '../../core/controls/range.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { LoadingState } from '../../core/panels/loading-panel.component';

@Component({
  selector: 'app-task-search',
  imports: [
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    RangeComponent,
  ],
  template: `
    <form [formGroup]="fg">
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2 sm:tw-gap-0 tw-mb-2 sm:tw-mb-0"
      >
        <app-input
          class="tw-w-full sm:tw-w-1/3 tw-min-w-0"
          [control]="fg.controls.title"
          label="Title"
        />
        <app-select
          [options]="taskStatusOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.taskStatus"
          label="Task Status"
        />
        <app-range
          label="Due Date"
          [control]="fg.controls.dueDateStart"
          [toControl]="fg.controls.dueDateEnd"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [compact]="false"
        ></app-range>
      </div>
      @if (
        fg.invalid &&
        (fg.dirty || fg.touched) &&
        fg.errors?.['bothOrNoneRequired']
      ) {
        <div
          class="tw-text-[var(--sed-status-red-color)] tw-font-bold tw-text-[80%] tw-px-4 tw-pt-1 tw-pb-2 tw-leading-[14px] app-fade-in"
        >
          Error: {{ fg.errors['bothOrNoneRequired'] }}
        </div>
      }

      <app-action-row useMobileLayout>
        <app-button
          right1
          type="submit"
          (click)="searchClick()"
          [loading]="loading"
          label="Search"
        />
        <app-button
          right2
          emphasis="Low"
          (click)="clearClick()"
          label="Clear"
        />
      </app-action-row>
    </form>
  `,
  styles: ``,
})
export class TaskSearchComponent extends BaseComponent implements OnInit {
  @Output() searchRequest = new EventEmitter<TaskRequest>();
  @Output() clear = new EventEmitter();

  @Input()
  loading = false;

  taskResponseData: TaskResponse[];
  protected state: LoadingState = 'Loading';

  fg = new FormGroup(
    {
      filterDate: new FormControl<Date>(new Date()),
      title: new FormControl<string>(null),
      taskStatus: new FormControl<string>('0'), // Default to 'All'
      dueDateStart: new FormControl<Date>(null),
      dueDateEnd: new FormControl<Date>(null),
    },
    {
      validators: [
        bothOrNoneRequired(
          'dueDateStart',
          'dueDateEnd',
          'Please specify a valid date range',
        ),
      ],
    },
  );

  taskStatusOptions = [
    { description: 'All', value: '0' },
    { description: 'Open', value: '1' },
    { description: 'Completed', value: '2' },
    { description: 'Past due', value: '3' },
  ];
  taskDueOptions = [
    { description: 'Due today', value: 'Today' },
    { description: 'Past due', value: 'Past' },
    { description: 'Future due', value: 'Future' },
  ];
  taskTypeOptions = [
    { description: 'TODO', value: 'TODO' },
    { description: 'One', value: 'One' },
    { description: 'Two', value: 'Two' },
  ];

  async ngOnInit() {
    this.state = 'Loaded';
  }

  protected searchClick() {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    } else {
      this.state = 'Loading';
      this.SearchTask();
    }
  }

  async clearClick() {
    this.fg.reset();
    this.clear.emit();
  }

  async SearchTask() {
    const taskStatusValue =
      this.fg.controls.taskStatus.value !== null
        ? Number(this.fg.controls.taskStatus.value)
        : 0;

    const request: TaskRequest = {
      title: this.fg.controls.title.value || '',
      taskStatus: taskStatusValue,
      dueStartDate: this.fg.controls.dueDateStart.value,
      dueEndDate: this.fg.controls.dueDateEnd.value,
      pastDue: taskStatusValue == 3 ? true : false,
    };

    this.searchRequest.emit(request);

    this.state = 'Loaded';
  }
}
